import { AlertCircle, CheckCircle2, Download, Loader2, Upload, X } from 'lucide-react';
import React, { useRef, useState } from 'react';
import type { BulkImportResultDto } from '../../types/api';
import { Button } from './Button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './Dialog';

interface BulkImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDownloadTemplate: () => void;
  onImport: (file: File) => Promise<BulkImportResultDto>;
  isDownloading?: boolean;
  isImporting?: boolean;
}

type Step = 'upload' | 'results';

export const BulkImportDialog: React.FC<BulkImportDialogProps> = ({
  open,
  onOpenChange,
  onDownloadTemplate,
  onImport,
  isDownloading = false,
  isImporting = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [step, setStep] = useState<Step>('upload');
  const [result, setResult] = useState<BulkImportResultDto | null>(null);
  const [fileError, setFileError] = useState('');

  const handleClose = () => {
    if (isImporting) return;
    setSelectedFile(null);
    setStep('upload');
    setResult(null);
    setFileError('');
    onOpenChange(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setFileError('');
    if (file && !file.name.endsWith('.csv')) {
      setFileError('Only .csv files are accepted');
      setSelectedFile(null);
      e.target.value = '';
      return;
    }
    setSelectedFile(file);
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    const data = await onImport(selectedFile);
    setResult(data);
    setStep('results');
  };

  const handleReset = () => {
    setSelectedFile(null);
    setStep('upload');
    setResult(null);
    setFileError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent showCloseButton={!isImporting} className='max-w-2xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <div className='flex items-start gap-3'>
            <Upload className='h-5 w-5 text-primary mt-0.5 flex-shrink-0' />
            <div>
              <DialogTitle>Bulk Import Users</DialogTitle>
              <DialogDescription className='mt-1'>
                Download the template, fill it in, then upload it to create multiple users at once.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {step === 'upload' && (
          <div className='space-y-5 pt-1'>
            {/* Step 1 — Download template */}
            <div className='rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4'>
              <p className='text-sm font-medium text-gray-700 mb-1'>Step 1 — Download the template</p>
              <p className='text-xs text-gray-500 mb-3'>
                The CSV requires three columns: <code className='bg-gray-100 px-1 rounded'>name</code>,{' '}
                <code className='bg-gray-100 px-1 rounded'>email</code>,{' '}
                <code className='bg-gray-100 px-1 rounded'>roleCode</code>.
              </p>
              <Button type='button' variant='outline' size='sm' onClick={onDownloadTemplate} disabled={isDownloading}>
                {isDownloading ? (
                  <Loader2 className='h-4 w-4 animate-spin mr-2' />
                ) : (
                  <Download className='h-4 w-4 mr-2' />
                )}
                Download template
              </Button>
            </div>

            {/* Step 2 — Upload file */}
            <div>
              <p className='text-sm font-medium text-gray-700 mb-2'>Step 2 — Upload your filled CSV</p>
              <label
                htmlFor='bulk-import-file'
                className={`flex flex-col items-center justify-center w-full h-28 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                  selectedFile
                    ? 'border-primary bg-secondary/30'
                    : 'border-gray-300 bg-gray-50 hover:border-primary hover:bg-secondary/10'
                } ${fileError ? 'border-red-400' : ''}`}
              >
                {selectedFile ? (
                  <div className='flex flex-col items-center gap-1'>
                    <CheckCircle2 className='h-6 w-6 text-primary' />
                    <span className='text-sm font-medium text-gray-700'>{selectedFile.name}</span>
                    <span className='text-xs text-gray-400'>{(selectedFile.size / 1024).toFixed(1)} KB</span>
                  </div>
                ) : (
                  <div className='flex flex-col items-center gap-1'>
                    <Upload className='h-6 w-6 text-gray-400' />
                    <span className='text-sm text-gray-500'>Click to select a .csv file</span>
                    <span className='text-xs text-gray-400'>Max 2 MB</span>
                  </div>
                )}
                <input
                  id='bulk-import-file'
                  ref={fileInputRef}
                  type='file'
                  accept='.csv'
                  className='hidden'
                  onChange={handleFileChange}
                  disabled={isImporting}
                />
              </label>
              {fileError && (
                <p className='mt-1 text-xs text-red-600 flex items-center gap-1'>
                  <AlertCircle className='h-3 w-3' />
                  {fileError}
                </p>
              )}
              {selectedFile && (
                <button
                  type='button'
                  onClick={() => {
                    setSelectedFile(null);
                    setFileError('');
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className='mt-1 text-xs text-gray-400 hover:text-red-500 flex items-center gap-1'
                  disabled={isImporting}
                >
                  <X className='h-3 w-3' />
                  Remove file
                </button>
              )}
            </div>
          </div>
        )}

        {step === 'results' && result && (
          <div className='space-y-4 pt-1'>
            {/* Summary chips */}
            <div className='flex gap-3'>
              <span className='inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium bg-green-100 text-green-700'>
                <CheckCircle2 className='h-4 w-4' />
                {result.created.length} created
              </span>
              {result.failed.length > 0 && (
                <span className='inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium bg-red-100 text-red-700'>
                  <AlertCircle className='h-4 w-4' />
                  {result.failed.length} failed
                </span>
              )}
            </div>

            {/* Created rows */}
            {result.created.length > 0 && (
              <div>
                <p className='text-sm font-medium text-gray-700 mb-2'>Successfully created</p>
                <div className='rounded-lg border border-green-100 overflow-hidden'>
                  <table className='min-w-full text-sm'>
                    <thead className='bg-green-50'>
                      <tr>
                        <th className='px-3 py-2 text-left text-xs font-medium text-green-700 uppercase tracking-wider'>Row</th>
                        <th className='px-3 py-2 text-left text-xs font-medium text-green-700 uppercase tracking-wider'>Name</th>
                        <th className='px-3 py-2 text-left text-xs font-medium text-green-700 uppercase tracking-wider'>Email</th>
                      </tr>
                    </thead>
                    <tbody className='bg-white divide-y divide-green-50'>
                      {result.created.map(r => (
                        <tr key={r.row}>
                          <td className='px-3 py-2 text-gray-500'>{r.row}</td>
                          <td className='px-3 py-2 text-gray-800'>{r.name ?? '-'}</td>
                          <td className='px-3 py-2 text-gray-800'>{r.email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Failed rows */}
            {result.failed.length > 0 && (
              <div>
                <p className='text-sm font-medium text-gray-700 mb-2'>Failed rows — fix and re-upload</p>
                <div className='rounded-lg border border-red-100 overflow-hidden'>
                  <table className='min-w-full text-sm'>
                    <thead className='bg-red-50'>
                      <tr>
                        <th className='px-3 py-2 text-left text-xs font-medium text-red-700 uppercase tracking-wider'>Row</th>
                        <th className='px-3 py-2 text-left text-xs font-medium text-red-700 uppercase tracking-wider'>Email</th>
                        <th className='px-3 py-2 text-left text-xs font-medium text-red-700 uppercase tracking-wider'>Reason</th>
                      </tr>
                    </thead>
                    <tbody className='bg-white divide-y divide-red-50'>
                      {result.failed.map(r => (
                        <tr key={r.row}>
                          <td className='px-3 py-2 text-gray-500'>{r.row}</td>
                          <td className='px-3 py-2 text-gray-800'>{r.email || '-'}</td>
                          <td className='px-3 py-2 text-red-600'>{r.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 'upload' ? (
            <>
              <Button type='button' variant='outline' onClick={handleClose} disabled={isImporting}>
                Cancel
              </Button>
              <Button type='button' onClick={handleImport} disabled={!selectedFile || isImporting}>
                {isImporting ? (
                  <>
                    <Loader2 className='h-4 w-4 animate-spin mr-2' />
                    Importing…
                  </>
                ) : (
                  <>
                    <Upload className='h-4 w-4 mr-2' />
                    Import users
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              {result && result.failed.length > 0 && (
                <Button type='button' variant='outline' onClick={handleReset}>
                  Upload another file
                </Button>
              )}
              <Button type='button' onClick={handleClose}>
                Done
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkImportDialog;
